import React, { useState } from 'react';
import { BookOpen, ChevronLeft, ChevronRight, Download } from 'lucide-react';

const ReadPage = () => {
  const [currentChapter, setCurrentChapter] = useState(1);

  const chapters = [
    { number: 1, title: "The Manuscript", content: `Dr. Elena Vasquez stared at the ancient manuscript spread across her desk, her fingers trembling slightly as she adjusted her glasses. The document was unlike anything she had ever encountered in her fifteen years as a cryptographer at the University of Amsterdam.

The manuscript had arrived three days ago, delivered by a nervous graduate student who claimed it had been found in the basement of a demolished monastery in Prague. The note accompanying it was brief: "Decode this, and you will change history."

Elena had spent countless hours studying historical ciphers—from the Caesar cipher of ancient Rome to the Enigma machine of World War II. But this... this was different. The symbols weren't just random characters; they followed a pattern, a rhythm that seemed almost alive.

She leaned closer to the screen, her custom decryption software running its 847th iteration. The manuscript contained 384 pages of what appeared to be religious text, but hidden within the margins were sequences of numbers and symbols that didn't match any known cipher system.

"Computer, enhance page 47," she said, her voice barely above a whisper.

The screen zoomed in on a series of numbers: 01101000 01100101 01101100 01110000 01110011.

Binary code. But what did it mean?

Elena quickly converted the binary to ASCII: "helps"

Her heart raced. This wasn't just a cipher; it was a message. A cry for help hidden in a centuries-old manuscript.

She worked through the night, decoding fragment after fragment. By dawn, she had pieced together a partial message:

"HELP US THEY ARE WATCHING THE CODE IS THE KEY TO EVERYTHING THEY STOLE OUR WORK THEY STOLE THE TRUTH"

Elena sat back in her chair, her mind reeling. What had she uncovered? And who was "they"?` },
    { number: 2, title: "Hidden in Plain Sight", content: `The next morning, Elena brought her findings to Professor Marcus Webb, the head of the cryptography department. Webb was a distinguished man in his sixties with a reputation for skepticism.

"Elena, this is fascinating," he said, peering over his glasses at her notes. "But you're suggesting that someone hid a modern distress call in a 17th-century manuscript?"

"Not just anyone," Elena replied, spreading out her decoded fragments. "Look at the pattern. The binary sequences are embedded in the margins using a steganographic technique that wouldn't have been possible before the digital age. This manuscript was altered recently—within the last few years."

Webb frowned. "So someone went to great lengths to hide a message in an ancient document. But why? And who is 'us'?"

"That's what I need to find out," Elena said. "Professor, I need access to the university's quantum decryption array. This cipher is more complex than I initially thought."

Webb hesitated. "The quantum array is reserved for classified government projects. You know that."

"Please," Elena insisted. "This could be important. Someone's life might depend on it."

After a long pause, Webb nodded. "I'll make some calls. But Elena—be careful. If someone went to this much trouble to hide a message, they probably don't want it found."` },
    { number: 3, title: "The Cipher", content: `That evening, Elena sat alone in the cryptography lab, the quantum decryption array humming softly beside her. She had uploaded the manuscript's digital scan and was running a full spectral analysis.

The results were staggering.

The manuscript contained not one cipher, but three—layered like Russian nesting dolls. The outer layer was a simple substitution cipher that any amateur could break. The middle layer required knowledge of 17th-century alchemical symbols. But the inner layer... the inner layer was something else entirely.

It was a quantum-resistant encryption algorithm, the kind used by intelligence agencies to protect their most sensitive communications. The kind that couldn't be broken by conventional computers.

Elena's fingers flew across the keyboard as she initiated the quantum decryption sequence. The array's processors whined as they worked through the astronomical number of possible keys.

Hours passed. Then, suddenly, the screen lit up with text.

"Project Lazarus. Subject: Digital consciousness transfer. Status: Active. Location: Vault 7, Prague. Warning: Unauthorized access will result in immediate termination."

Elena's blood ran cold. Digital consciousness transfer? That was theoretical—impossible, even. Yet here was documentation suggesting it was not only possible but operational.

She quickly copied the data to an encrypted drive. As she did, her screen flickered. A message appeared:

"YOU SHOULD NOT HAVE FOUND THIS. STOP NOW OR FACE THE CONSEQUENCES."

Elena's hands shook as she yanked the USB drive from the computer. Someone was watching. Someone knew what she had discovered.` },
  ];

  const chapter = chapters.find(c => c.number === currentChapter) || chapters[0];

  return (
    <div>
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Read the Book</h1>
          <p className="text-xl opacity-90">The Lost Code Mystery - A Techno-Thriller by Victor Grösser</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Chapter Navigation */}
            <div className="mb-8 flex items-center justify-between">
              <button
                onClick={() => setCurrentChapter(Math.max(1, currentChapter - 1))}
                disabled={currentChapter === 1}
                className="flex items-center text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed hover:text-amber-800"
              >
                <ChevronLeft /> Previous
              </button>
              <div className="text-lg font-semibold">
                Chapter {currentChapter}: {chapter.title}
              </div>
              <button
                onClick={() => setCurrentChapter(Math.min(chapters.length, currentChapter + 1))}
                disabled={currentChapter === chapters.length}
                className="flex items-center text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed hover:text-amber-800"
              >
                Next <ChevronRight />
              </button>
            </div>

            {/* Chapter Content */}
            <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                {chapter.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-6 text-gray-800 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Chapter List */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold mb-6">All Chapters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {chapters.map((ch) => (
                  <button
                    key={ch.number}
                    onClick={() => setCurrentChapter(ch.number)}
                    className={`p-4 rounded-lg text-left transition ${
                      currentChapter === ch.number
                        ? 'bg-amber-100 border-2 border-amber-500'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <span className="font-semibold">Chapter {ch.number}:</span> {ch.title}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <p className="text-lg text-gray-600 mb-6">
                Want to read the complete novel? Get your copy today!
              </p>
              <a href="/buy" className="bg-amber-500 text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-amber-400 transition inline-flex items-center">
                <Download className="mr-2" /> Buy Full Book - $14.99
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReadPage;